import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from 'reactstrap';
import { ImageIcon } from '@saleshandy/design-system';
import Modal from '../design-system/components/atoms/modal';
import Button from '../design-system/ui/button';
import { Images } from '../app-constants';

const TagEditorModal = ({
  show = false,
  onClose,
  onHide,
  hideHeader = false,
  hideFooter = false,
  modalClassName = '',
  mergeTagId = '',
  onContentChange,
  editorInstance,
  ...modalProps
}: {
  show: boolean;
  onClose: () => void;
  onHide: () => void;
  hideHeader: boolean;
  hideFooter: boolean;
  modalClassName: string;
  mergeTagId: string;
  onContentChange: () => void;
  editorInstance: any;
  [key: string]: any;
}) => {
  const mergeTagRef = useRef<HTMLElement>(null);

  const [mergeTagValue, setMergeTagValue] = useState({
    tagValue: '',
    tagFallback: '',
    tagFallbackHasError: false,
    tagFallbackDirty: false,
    tagFieldType: '',
  });
  const decodeHtmlEntities = useCallback(
    (value: string) =>
      (value || '')
        ?.replace(/&lt;/g, '<')
        ?.replace(/&gt;/g, '>'),
    [],
  );
  
  const handleSaveValue = useCallback(() => {
    const isLongTextFieldType =
      mergeTagValue?.tagFieldType === 'long-text';
    const fallbackValue = isLongTextFieldType
      ? mergeTagValue?.tagFallback ?? ''
      : mergeTagValue?.tagFallback?.trim() ?? '';

    if (fallbackValue) {
      const hasSpinTags =
        fallbackValue.includes('{spin}') ||
        fallbackValue.includes('{endspin}');

      if (hasSpinTags) {
        setMergeTagValue((prev) => ({
          ...prev,
          tagFallbackHasError: true,
        }));
        return;
      }
    }

    if (mergeTagRef?.current) {
      const element = mergeTagRef.current;

      const chipElement = element?.querySelector('.merge-tag-chip');
      const fallbackElement = element?.querySelector('.editable-fallback');

      if (chipElement) {
        chipElement.innerHTML = mergeTagValue?.tagValue || '';
      }
      if (fallbackElement) {
        if (isLongTextFieldType) {
          if (fallbackValue) {
            fallbackElement.textContent = fallbackValue;
            fallbackElement.classList.remove('default-fallback');
            fallbackElement.setAttribute('data-raw-fallback', fallbackValue);
          } else {
            fallbackElement.textContent = '';
            fallbackElement.classList.add('default-fallback');
            fallbackElement.removeAttribute('data-raw-fallback');
          }
        } else if (fallbackValue) {
          fallbackElement.innerHTML = fallbackValue;
          fallbackElement.classList.remove('default-fallback');
          fallbackElement.removeAttribute('data-raw-fallback');
        } else {
          fallbackElement.innerHTML = '';
          fallbackElement.classList.add('default-fallback');
          fallbackElement.removeAttribute('data-raw-fallback');
        }
      }

      // Force rerender after value changes and restore cursor position
      setTimeout(() => {
        onContentChange();
      }, 100);
    }
    onClose();
  }, [
    mergeTagValue,
    onClose,
    onContentChange,
    editorInstance,
    mergeTagId,
  ]);

  useEffect(() => {
    if (mergeTagId) {
      const ele = document.getElementById(mergeTagId);
      mergeTagRef.current = ele;
      const element = mergeTagRef.current;
      if (element) {
        const tagValue =
          element.querySelector('.merge-tag-chip')?.innerHTML || '';
        const fallbackElement = element.querySelector(
          '.editable-fallback',
        ) as HTMLElement | null;
        const fieldTypeFromElement =
          element.getAttribute('data-field-type') || '';
        const isLongTextFieldType = fieldTypeFromElement === 'long-text';
        const rawFallback = isLongTextFieldType
          ? fallbackElement?.getAttribute('data-raw-fallback') ??
            fallbackElement?.textContent ??
            ''
          : fallbackElement?.innerHTML || '';
        const normalizedFallback = isLongTextFieldType
          ? decodeHtmlEntities(rawFallback || '')
          : rawFallback || '';
        setMergeTagValue({
          tagValue: tagValue || '',
          tagFallback: normalizedFallback,
          tagFallbackHasError: false,
          tagFallbackDirty: false,
          tagFieldType: fieldTypeFromElement || '',
        });
      }
    }
  }, [mergeTagId]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      onHide={onHide}
      titleContent={<h1>Add Fallback Text</h1>}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      className={modalClassName}
      {...modalProps}
    >
      <div className="merge-tag-modal-container">
        <div className="merge-tag-modal-container__header">
          <div className="merge-tag-modal-container__header-title">
            {`{{}} Add Fallback Text`}
          </div>
        </div>

        <div className="merge-tag-modal-container__body">
          <div className="fallback-prompt">
            Looks like <strong>{mergeTagValue?.tagValue?.trim() || ''}</strong>{' '}
            might be missing sometimes. Add fallback text to show instead.
          </div>
        </div>

        <div className="merge-tag-modal-container__add-fallback">
          <span className="fallback-prompt">
            {mergeTagValue?.tagValue?.trim() || ''}
          </span>
          <div className="add-fallback-text">
            <Input
              type={
                mergeTagValue?.tagFieldType === 'long-text'
                  ? 'textarea'
                  : 'text'
              }
              label="Fallback Text"
              placeholder="Type fallback text"
              rows={mergeTagValue?.tagFieldType === 'long-text' ? 6 : undefined}
              className={`${
                mergeTagValue?.tagFallbackHasError ? 'tag-fallback-error' : ''
              }`}
              value={mergeTagValue?.tagFallback || ''}
              onChange={(e) => {
                const value = e?.target?.value || '';
                const isLongTextFieldType =
                  mergeTagValue?.tagFieldType === 'long-text';
                 const regex =/\{\{.*?\}\}|\{spin\}[\s\S]*?\{endspin\}|\{\{|\}\}|\{|\}|<|>/;
                const longTextRegex =
                  /\{\{.*?\}\}|\{spin\}[\s\S]*?\{endspin\}|[{}]/;
                const hasError = isLongTextFieldType
                  ? longTextRegex.test(value) || value?.length > 10000
                  : regex.test(value);
                setMergeTagValue((prev) => ({
                  ...prev,
                  tagFallback: value,
                  tagFallbackHasError: hasError,
                  tagFallbackDirty: true,
                }));
              }}
            />
            {mergeTagValue?.tagFallbackHasError && (
              <div className="fallback-text-error-container">
                <ImageIcon src={Images.AlertTriangleRed} alt="Alert" />
                <div className="fallback-text-error">
                  {mergeTagValue?.tagFieldType === 'long-text'
                    ? 'Please enter valid fallback text, no merge tags, spin tags, or curly braces allowed.'
                    : 'Please enter valid fallback text, no merge tags, spin tags or html allowed.'}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="merge-tag-modal-container__footer">
          <Button
            className="merge-tag-modal-container__footer-cancel-button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={mergeTagValue?.tagFallbackHasError}
            className="merge-tag-modal-container__footer-submit-button"
            onClick={handleSaveValue}
          >
            Add
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(TagEditorModal);
